// The phone's touch timeline (spec docs/specs/editor/phone-timeline.md): the gestures on the
// timeline's scroller. Every one ends in what the editor has already: a seek, a zoom, or the edit
// the desktop's drop makes, through the drag functions `Tracks` hands in.
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

import {
  LONG_PRESS_MS,
  LONG_PRESS_SLOP_PX,
  edgeStepPx,
  pinchZoom,
  scrollForTime,
  timeAtCentre,
} from "./touch-timeline";

/** What `Tracks` lends a finger's drag: the very functions the browser's drag runs. */
export interface TouchDrag {
  /** Lifts the clip of `block` where the finger holds it; false when it may not move. */
  lift: (block: HTMLElement, clientX: number) => boolean;
  /** The lifted clip over the place under the finger: its landing shown, as the desktop's. */
  over: (clientX: number, clientY: number) => void;
  /** Let go there: the desktop drop's own edit, or none off every band. */
  drop: (clientX: number, clientY: number) => void;
  cancel: () => void;
}

export interface TouchTimeline {
  pxPerSecond: number;
  playheadMs: number;
  /** The project's length: a scroll seeks no further, as the ruler's seeks. */
  totalMs: number;
  onSeek: (ms: number) => void;
  zoom: number;
  onZoom: (zoom: number) => void;
  drag: TouchDrag;
}

// A scroll the user made, so the playhead does not scroll it back for this long after.
const USER_SCROLL_MS = 150;
// A lifted clip held this near the timeline's edge scrolls it (`edgeStepPx`, each frame as far as
// its time): a phone shows little of the timeline either side of the middle.
const EDGE_PX = 40;

/**
 * Mounted by `Tracks` on a phone, given its scroller; `options` undefined off a phone does nothing.
 * Its answer is what the timeline shows of the gestures: the pinch's zoom, and whether a clip is
 * lifted.
 */
export function useTouchTimeline(
  scroller: RefObject<HTMLDivElement | null>,
  options: TouchTimeline | undefined,
): { pinching: number | null; lifted: boolean } {
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });
  const on = options !== undefined;
  const [pinching, setPinching] = useState<number | null>(null);
  const [lifted, setLifted] = useState(false);
  // The scroll this hook set last, so its own scroll seeks nothing; and when the user last scrolled.
  const ours = useRef<number | null>(null);
  const userAt = useRef(0);
  // Whether a clip is lifted: the playhead waits for its drop to settle.
  const liftedNow = useRef(false);

  // A scroll the user makes seeks to the time under the middle, at most once a frame, and never past
  // the project's end, as the ruler's seeks. The lanes reach past the end, room to drop a clip, so
  // once the scroll is done the middle marks the playhead again.
  useEffect(() => {
    const element = scroller.current;
    if (!on || element === null) return undefined;
    let frame = 0;
    let settle = 0;
    // The time this scroll last sought.
    let sought = Number.NaN;
    const settled = () => {
      settle = 0;
      const now = latest.current;
      if (now === undefined) return;
      if (liftedNow.current) {
        settle = window.setTimeout(settled, USER_SCROLL_MS);
        return;
      }
      const centre = timeAtCentre(element.scrollLeft, now.pxPerSecond);
      // The playhead where this scroll left it, and the middle inside the project (a drop past the
      // end lengthened it): the playhead goes to the middle.
      if (now.playheadMs === sought && centre <= now.totalMs) {
        if (Math.abs(centre - now.playheadMs) >= 1) now.onSeek(centre);
        return;
      }
      // Past the end, where the seek stopped, or the playhead moved since (Play, the ruler): it comes
      // back under the middle.
      const target = scrollForTime(now.playheadMs, now.pxPerSecond);
      if (Math.abs(element.scrollLeft - target) < 1) return;
      ours.current = target;
      element.scrollTo({ left: target });
    };
    const scrolled = () => {
      const set = ours.current;
      if (set !== null && Math.abs(element.scrollLeft - set) < 1) return;
      ours.current = null;
      userAt.current = performance.now();
      window.clearTimeout(settle);
      settle = window.setTimeout(settled, USER_SCROLL_MS);
      if (frame !== 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const now = latest.current;
        if (now === undefined) return;
        sought = Math.min(timeAtCentre(element.scrollLeft, now.pxPerSecond), now.totalMs);
        now.onSeek(sought);
      });
    };
    element.addEventListener("scroll", scrolled, { passive: true });
    return () => {
      element.removeEventListener("scroll", scrolled);
      if (frame !== 0) cancelAnimationFrame(frame);
      window.clearTimeout(settle);
    };
  }, [on, scroller]);

  // A seek, the play clock or a zoom puts the playhead's time under the middle, unless the user is
  // scrolling it there.
  const playheadMs = options?.playheadMs ?? 0;
  const pxPerSecond = options?.pxPerSecond ?? 0;
  useLayoutEffect(() => {
    const element = scroller.current;
    if (!on || element === null || performance.now() - userAt.current < USER_SCROLL_MS) return;
    const target = scrollForTime(playheadMs, pxPerSecond);
    if (Math.abs(element.scrollLeft - target) < 1) return;
    ours.current = target;
    element.scrollTo({ left: target });
  }, [on, scroller, playheadMs, pxPerSecond]);

  // Two fingers pinch the zoom; one held still on a clip lifts it. Touch events, so one finger
  // keeps the browser's own scroll until a clip is lifted.
  useEffect(() => {
    const element = scroller.current;
    if (!on || element === null) return undefined;
    let pinch: { zoom: number; distance: number } | null = null;
    // The pinch's zoom, set once a frame: each zoom lays the whole timeline out again.
    let zoomed: number | null = null;
    let zoomFrame = 0;
    const applyZoom = () => {
      zoomFrame = 0;
      if (zoomed === null) return;
      latest.current?.onZoom(zoomed);
      setPinching(zoomed);
      zoomed = null;
    };
    let press: { x: number; y: number; block: HTMLElement; timer: number } | null = null;
    let holding = false;
    // Where the finger lifted the clip, on the timeline's content: let go there, it makes no edit.
    let liftedAt = { x: 0, y: 0 };
    // Where the lifted clip's finger is, and the edge scroll that follows it: which edge (-1 or 1,
    // 0 for none), a step a frame as far as the frame's time since the last (`edgeAt`).
    let finger = { x: 0, y: 0 };
    let edge = 0;
    let edgeFrame = 0;
    let edgeAt: number | null = null;
    const edgeScroll = (now: number) => {
      edgeFrame = 0;
      if (!holding || edge === 0) {
        edgeAt = null;
        return;
      }
      element.scrollBy({ left: edge * edgeStepPx(edgeAt === null ? null : now - edgeAt) });
      edgeAt = now;
      latest.current?.drag.over(finger.x, finger.y);
      edgeFrame = requestAnimationFrame(edgeScroll);
    };
    const stopEdge = () => {
      edge = 0;
      edgeAt = null;
      if (edgeFrame !== 0) cancelAnimationFrame(edgeFrame);
      edgeFrame = 0;
    };
    const spread = (touches: TouchList) => {
      const [one, other] = [touches[0], touches[1]];
      return one === undefined || other === undefined
        ? 0
        : Math.hypot(one.clientX - other.clientX, one.clientY - other.clientY);
    };
    const letGo = () => {
      if (press !== null) window.clearTimeout(press.timer);
      press = null;
    };
    const lift = () => {
      const held = press;
      const now = latest.current;
      press = null;
      if (held === null || now === undefined) return;
      holding = now.drag.lift(held.block, held.x);
      liftedNow.current = holding;
      if (!holding) return;
      liftedAt = { x: held.x + element.scrollLeft, y: held.y };
      setLifted(true);
      navigator.vibrate?.(10);
    };
    const start = (event: TouchEvent) => {
      const now = latest.current;
      if (now === undefined) return;
      if (event.touches.length === 2 && !holding) {
        letGo();
        pinch = { zoom: now.zoom, distance: spread(event.touches) };
        setPinching(now.zoom);
        return;
      }
      const touch = event.touches[0];
      if (event.touches.length !== 1 || touch === undefined) return;
      const block = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-block]") : null;
      if (block === null) return;
      press = { x: touch.clientX, y: touch.clientY, block, timer: window.setTimeout(lift, LONG_PRESS_MS) };
    };
    const move = (event: TouchEvent) => {
      const now = latest.current;
      if (now === undefined) return;
      if (pinch !== null && event.touches.length >= 2) {
        // The fingers are the zoom's, not the browser's.
        event.preventDefault();
        zoomed = pinchZoom(pinch.zoom, pinch.distance, spread(event.touches));
        if (zoomFrame === 0) zoomFrame = requestAnimationFrame(applyZoom);
        return;
      }
      const touch = event.touches[0];
      if (touch === undefined) return;
      if (holding) {
        // A lifted clip follows the finger, and the timeline holds still under it, but at its edges.
        event.preventDefault();
        finger = { x: touch.clientX, y: touch.clientY };
        const box = element.getBoundingClientRect();
        edge = finger.x < box.left + EDGE_PX ? -1 : finger.x > box.right - EDGE_PX ? 1 : 0;
        if (edge !== 0 && edgeFrame === 0) edgeFrame = requestAnimationFrame(edgeScroll);
        now.drag.over(finger.x, finger.y);
        return;
      }
      if (press !== null && Math.hypot(touch.clientX - press.x, touch.clientY - press.y) >= LONG_PRESS_SLOP_PX) {
        // A finger that moves first is a scroll.
        letGo();
      }
    };
    const end = (event: TouchEvent) => {
      if (pinch !== null && event.touches.length < 2) {
        // The last spread counts, then the readout goes.
        if (zoomFrame !== 0) cancelAnimationFrame(zoomFrame);
        applyZoom();
        pinch = null;
        setPinching(null);
      }
      if (holding) {
        // No click after a drag: the drop is the gesture's end. Let go where it was lifted (the
        // timeline under it unmoved), the clip stays: no edit, not even a snap.
        event.preventDefault();
        stopEdge();
        const touch = event.changedTouches[0];
        const still =
          touch === undefined ||
          Math.hypot(touch.clientX + element.scrollLeft - liftedAt.x, touch.clientY - liftedAt.y) < LONG_PRESS_SLOP_PX;
        if (touch === undefined || still) latest.current?.drag.cancel();
        else latest.current?.drag.drop(touch.clientX, touch.clientY);
        holding = false;
        liftedNow.current = false;
        setLifted(false);
      }
      letGo();
    };
    const cancel = () => {
      stopEdge();
      // A zoom still waiting for its frame would show the readout again, with no fingers down.
      if (zoomFrame !== 0) cancelAnimationFrame(zoomFrame);
      zoomFrame = 0;
      zoomed = null;
      if (holding) latest.current?.drag.cancel();
      holding = false;
      liftedNow.current = false;
      pinch = null;
      setLifted(false);
      setPinching(null);
      letGo();
    };
    // A long-press is a lift here, not the phone's own menu.
    const noMenu = (event: Event) => {
      if (press === null && !holding) return;
      event.preventDefault();
      event.stopPropagation();
    };
    element.addEventListener("touchstart", start, { passive: true });
    element.addEventListener("touchmove", move, { passive: false });
    element.addEventListener("touchend", end, { passive: false });
    element.addEventListener("touchcancel", cancel);
    element.addEventListener("contextmenu", noMenu, true);
    return () => {
      letGo();
      stopEdge();
      liftedNow.current = false;
      if (zoomFrame !== 0) cancelAnimationFrame(zoomFrame);
      element.removeEventListener("touchstart", start);
      element.removeEventListener("touchmove", move);
      element.removeEventListener("touchend", end);
      element.removeEventListener("touchcancel", cancel);
      element.removeEventListener("contextmenu", noMenu, true);
    };
  }, [on, scroller]);

  return { pinching, lifted };
}
